// Board themes (plan §4, ask #5). The board squares are painted by a
// self-contained conic-gradient checker in `style.css`, driven by CSS custom
// properties; a `data-board` attribute on `<html>` selects the palette. The
// choice persists in localStorage and a live board repaints instantly (no
// chessground re-init). No external assets, so COEP stays clean.

import { t } from "../i18n/index.ts";
import { listenWhileMounted } from "./lifecycle.ts";

export interface BoardTheme {
  id: string;
  label: string; // English fallback; the picker shows the localized board.<id>
  light: string; // light-square colour (also the swatch preview)
  dark: string; // dark-square colour
}

/** Green (chess.com default) + two popular alternates. */
export const BOARD_THEMES: BoardTheme[] = [
  { id: "green", label: "Green", light: "#eeeed2", dark: "#769656" },
  { id: "brown", label: "Brown", light: "#f0d9b5", dark: "#b58863" },
  { id: "blue", label: "Blue", light: "#dee3e6", dark: "#8ca2ad" },
];

const STORAGE_KEY = "board-theme";
const DEFAULT_ID = "green";
let memory: string | undefined;

/** The stored (or default) board theme id, always one we actually ship. */
export function currentBoardTheme(): string {
  if (memory) return memory;
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch {
    /* private mode / disabled storage → default */
  }
  return BOARD_THEMES.some((bt) => bt.id === stored) ? (stored as string) : DEFAULT_ID;
}

/** Stamp the current board theme onto `<html data-board>`. Call once at boot. */
export function applyBoardTheme(id: string = currentBoardTheme()): void {
  document.documentElement.setAttribute("data-board", id);
}

/** Persist + apply a board theme (instant repaint via the `data-board` swap). */
export function setBoardTheme(id: string): void {
  const theme = BOARD_THEMES.find((bt) => bt.id === id) ? id : DEFAULT_ID;
  memory = theme;
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* non-fatal: the attribute still applies for this session */
  }
  applyBoardTheme(theme);
  window.dispatchEvent(new Event("boardthemechange"));
}

/** Inline style for a mini-board swatch drawn with the same conic checker. */
function swatchStyle(bt: BoardTheme): string {
  return (
    `background-color:${bt.light};` +
    `background-image:conic-gradient(${bt.dark} 90deg,${bt.light} 90deg 180deg,` +
    `${bt.dark} 180deg 270deg,${bt.light} 270deg);` +
    `background-size:50% 50%;`
  );
}

/** A radiogroup of live board-theme swatches for Settings (plan §4.3). */
export function boardThemePicker(): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "board-picker";
  wrap.setAttribute("role", "radiogroup");
  wrap.setAttribute("aria-label", t("board.pickerLabel"));

  const current = currentBoardTheme();
  for (const bt of BOARD_THEMES) {
    const label = document.createElement("label");
    label.className = "board-swatch" + (bt.id === current ? " active" : "");
    label.innerHTML =
      `<input type="radio" name="board-theme" value="${bt.id}" ${bt.id === current ? "checked" : ""} />` +
      `<span class="sw" style="${swatchStyle(bt)}"></span>` +
      `<small>${t("board." + bt.id)}</small>`;
    wrap.append(label);
  }

  wrap.addEventListener("change", () => {
    const checked = wrap.querySelector<HTMLInputElement>('input[name="board-theme"]:checked');
    if (!checked) return;
    setBoardTheme(checked.value);
    wrap.querySelectorAll<HTMLElement>(".board-swatch").forEach((el) => {
      const input = el.querySelector<HTMLInputElement>("input")!;
      el.classList.toggle("active", input.checked);
    });
  });
  listenWhileMounted(wrap, "boardthemechange", () => {
    wrap.querySelectorAll<HTMLInputElement>("input").forEach(input => {
      input.checked = input.value === currentBoardTheme();
      input.closest("label")!.classList.toggle("active", input.checked);
    });
  });

  return wrap;
}
